// Excel-style category filters for Watchlist attribute columns.
//
// Each column has at most one filter, { keys, blank }:
// - `keys` lists the category keys (categoryKey) whose rows stay visible, or
//   is null for every category, including ones typed later;
// - `blank` keeps rows with no category.
// { keys: null, blank: true } is "no filter". Filters match saved values, as
// the quick filter does, so a row does not vanish while a cell is edited.
import { categoryKey } from './categoryTools';

export const BLANK_CATEGORY_LABEL = '(Blank)';

/** Every category and blanks: the column is not filtered. */
export function selectAllCategories() {
    return { keys: null, blank: true };
}

/** Nothing ticked: the column shows no rows until something is ticked. */
export function clearAllCategories() {
    return { keys: [], blank: false };
}

function normalizeFilter(filter) {
    if (!filter) return selectAllCategories();
    return {
        keys: Array.isArray(filter.keys) ? filter.keys : null,
        blank: filter.blank !== false,
    };
}

export function isCategoryFilterActive(filter) {
    return Boolean(filter) && (Array.isArray(filter.keys) || filter.blank === false);
}

/** Whether a category is ticked; a blank value or '' means the (Blank) option. */
export function isCategorySelected(filter, value) {
    if (!isCategoryFilterActive(filter)) return true;
    const key = categoryKey(value);
    if (!key) return filter.blank !== false;
    return !Array.isArray(filter.keys) || filter.keys.includes(key);
}

export function rowMatchesCategoryFilter(savedValue, filter) {
    return isCategorySelected(filter, savedValue);
}

/**
 * Whether a row passes every column's filter. `filters` maps a column id to
 * its filter; `readSavedValue(columnId)` reads the row's saved value.
 */
export function rowMatchesCategoryFilters(readSavedValue, filters) {
    if (!filters) return true;
    return Object.keys(filters).every((columnId) => {
        const filter = filters[columnId];
        return !isCategoryFilterActive(filter) || isCategorySelected(filter, readSavedValue(columnId));
    });
}

/**
 * The option keys a column offers in the filter list: its categories, plus ''
 * for "(Blank)" when it has blank rows. This is the `allKeys` argument below.
 */
export function categoryFilterOptionKeys(facets) {
    const keys = (facets?.categories || []).map((category) => category.key);
    return facets?.blankCount > 0 ? [...keys, ''] : keys;
}

/**
 * Ticks (selected = true) or unticks several options at once; '' is the
 * "(Blank)" option. `allKeys` are every option the column offers
 * (categoryFilterOptionKeys). Once every option is ticked the filter switches
 * off, so categories typed later show too.
 */
export function setCategoriesSelected(filter, keys, selected, allKeys) {
    const current = normalizeFilter(filter);
    const targets = new Set(keys.map(categoryKey));
    const options = new Set(allKeys.map(categoryKey));
    const blank = targets.has('') ? selected : current.blank;
    targets.delete('');
    const categoryOptions = [...options].filter(Boolean);

    let nextKeys;
    if (current.keys === null) {
        const remaining = categoryOptions.filter((key) => !targets.has(key));
        nextKeys = selected || remaining.length === categoryOptions.length ? null : remaining;
    } else if (selected) {
        nextKeys = [...new Set([...current.keys, ...targets])];
    } else {
        nextKeys = current.keys.filter((key) => !targets.has(key));
    }

    const everyOptionTicked = (blank || !options.has(''))
        && (nextKeys === null || categoryOptions.every((key) => nextKeys.includes(key)));
    return everyOptionTicked ? selectAllCategories() : { keys: nextKeys, blank };
}

/** Ticks or unticks one option ('' is "(Blank)"). */
export function toggleCategoryKey(filter, key, allKeys) {
    return setCategoriesSelected(filter, [key], !isCategorySelected(filter, key), allKeys);
}

/**
 * A short label for a filter chip: the ticked names when there are one or
 * two ("AI, Energy", "(Blank)"), otherwise a count ("3 of 7"). '' when the
 * column is not filtered. `facets` come from categoryFacets.
 */
export function describeCategoryFilter(filter, facets) {
    if (!isCategoryFilterActive(filter)) return '';
    const categories = facets?.categories || [];
    const hasBlankOption = facets?.blankCount > 0;
    const ticked = categories
        .filter((category) => isCategorySelected(filter, category.key))
        .map((category) => category.value);
    if (hasBlankOption && filter.blank !== false) ticked.push(BLANK_CATEGORY_LABEL);
    if (ticked.length === 0) return 'Nothing selected';
    if (ticked.length <= 2) return ticked.join(', ');
    return `${ticked.length} of ${categories.length + (hasBlankOption ? 1 : 0)}`;
}

/**
 * Keeps renamed rows in view: when the renamed category is ticked, its new
 * category (or "(Blank)" after clearing) is ticked too. The old key stays
 * ticked because filters match saved values until the rename is saved.
 */
export function applyCategoryRenameToFilter(filter, fromValue, toValue) {
    if (!isCategoryFilterActive(filter)
        || !isCategorySelected(filter, fromValue)
        || isCategorySelected(filter, toValue)) {
        return filter;
    }
    const toKey = categoryKey(toValue);
    if (!toKey) return { ...filter, blank: true };
    return { ...filter, keys: [...filter.keys, toKey] };
}
